import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef } from "react";

import {
  fetchLineageDsm,
  type LineageDsmRequest,
  type LineageDsmResult,
  type LineageDsmMatrixKind,
} from "../../api/lineageDsm";
import { compareInstantsAtMicrosecondPrecision } from "../../lib/temporalInstant";
import type { DagFilterState } from "./search";
import { buildCanonicalLineageQueryRequest } from "./useCanonicalLineageProjection";
import { parseCanonicalLineageRefKey } from "./canonicalAdapter";

export interface LineageDsmDescriptor {
  projectId: string;
  matrixKind: LineageDsmMatrixKind;
  drillToken: string | null;
  cursor: string | null;
  selectedId: string | null;
  searchQuery: string;
  filters: DagFilterState;
  asOf: string | null;
  recordedAsOf: string | null;
  snapshotToken: string | null;
}

export interface LineageDsmTemporalCut {
  effectiveAsOf: string;
  recordedAsOf: string;
  snapshotToken: string;
}

export interface LineageDsmState {
  result: LineageDsmResult | null;
  issuedCut: LineageDsmTemporalCut | null;
  isPending: boolean;
  isFetching: boolean;
  isExactSettled: boolean;
  error: Error | null;
  refresh: () => Promise<unknown>;
}

/** 31² = 961, leaving the strict 1,000-cell response budget intact. */
export const LINEAGE_DSM_UI_LEAF_PAGE_SIZE = 31;

function exactArtifactVersionId(value: string | null): string | null {
  const parsed = value ? parseCanonicalLineageRefKey(value) : null;
  return parsed?.entity_kind === "artifact_version" ? parsed.id : null;
}

/**
 * Build one bounded DSM request. Search/filter compilation is shared with the
 * canonical discovery projection, but no graph request is made or retained.
 */
export function buildLineageDsmRequest(
  descriptor: LineageDsmDescriptor,
  now = new Date(),
): LineageDsmRequest {
  const hasRecorded = descriptor.recordedAsOf !== null;
  const hasSnapshot = descriptor.snapshotToken !== null;
  if (hasRecorded !== hasSnapshot || (hasRecorded && descriptor.asOf === null)) {
    throw new Error("A frozen DSM replay requires effective time, recorded time, and snapshot token together.");
  }
  if (descriptor.cursor !== null && descriptor.drillToken === null) {
    throw new Error("A DSM leaf cursor requires its signed drill path.");
  }
  if (descriptor.drillToken !== null && (!hasRecorded || descriptor.asOf === null)) {
    throw new Error("A DSM drill path requires the exact temporal replay trio.");
  }
  const discovery = buildCanonicalLineageQueryRequest({
    projectId: descriptor.projectId,
    mode: "chronicle",
    direction: "both",
    depth: 1,
    selectedId: null,
    compareA: null,
    compareB: null,
    summaryGroup: "category",
    searchQuery: descriptor.searchQuery,
    filters: descriptor.filters,
    asOf: descriptor.asOf,
    recordedAsOf: descriptor.recordedAsOf,
    snapshotToken: descriptor.snapshotToken,
  }, now);
  const focusId = descriptor.drillToken === null ? exactArtifactVersionId(descriptor.selectedId) : null;
  return {
    project_id: descriptor.projectId,
    matrix_kind: descriptor.matrixKind,
    ...(discovery.query ? { query: discovery.query } : {}),
    filters: discovery.filters ?? {},
    ...(descriptor.drillToken ? { drill_token: descriptor.drillToken } : {}),
    ...(descriptor.cursor ? { cursor: descriptor.cursor } : {}),
    ...(focusId ? { focus_ref: { entity_kind: "artifact_version", id: focusId } } : {}),
    page_size: LINEAGE_DSM_UI_LEAF_PAGE_SIZE,
    as_of: descriptor.asOf,
    ...(descriptor.recordedAsOf && descriptor.snapshotToken ? {
      recorded_as_of: descriptor.recordedAsOf,
      snapshot_token: descriptor.snapshotToken,
    } : {}),
  };
}

function sameJson(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

/** Mirror the server's signed-token breadcrumb spelling exactly. */
function compactLineageDsmBreadcrumbLabel(value: string): string {
  const maximum = 16;
  const pieces: string[] = [];
  for (let index = 0; index < value.length;) {
    const escapePrefix = value.slice(index, index + 2);
    if (escapePrefix === "\\u" || escapePrefix === "\\U") {
      const width = escapePrefix === "\\u" ? 6 : 10;
      const candidate = value.slice(index, index + width);
      if (candidate.length === width && /^[0-9a-fA-F]+$/.test(candidate.slice(2))) {
        pieces.push(candidate);
        index += width;
        continue;
      }
    }
    const codePoint = value.codePointAt(index);
    if (codePoint === undefined) break;
    const character = String.fromCodePoint(codePoint);
    if (codePoint >= 0x20 && codePoint <= 0x7e) {
      pieces.push(character);
    } else if (codePoint <= 0xffff) {
      pieces.push(`\\u${codePoint.toString(16).toUpperCase().padStart(4, "0")}`);
    } else {
      pieces.push(`\\U${codePoint.toString(16).toUpperCase().padStart(8, "0")}`);
    }
    index += character.length;
  }
  if (pieces.reduce((length, piece) => length + piece.length, 0) <= maximum) {
    return pieces.join("");
  }
  const budget = maximum - 3;
  const rendered: string[] = [];
  let used = 0;
  for (const piece of pieces) {
    if (used + piece.length > budget) break;
    rendered.push(piece);
    used += piece.length;
  }
  return `${rendered.join("")}...`;
}

/** Fail closed if a cursor response escapes the exact first-page disclosure. */
export function validateLineageDsmContinuation(
  previous: LineageDsmResult,
  next: LineageDsmResult,
  cursor: string,
): void {
  if (previous.next_cursor !== cursor) throw new Error("DSM continuation cursor does not follow the visible leaf page.");
  if (previous.level_kind !== "leaves" || next.level_kind !== "leaves") {
    throw new Error("Only exact DSM leaf pages may continue.");
  }
  const fields: Array<keyof LineageDsmResult> = [
    "contract_version",
    "resource_limits_version",
    "project_id",
    "matrix_kind",
    "aggregation_boundary",
    "applied_selection",
    "effective_as_of",
    "recorded_as_of",
    "snapshot_token",
    "level",
    "level_kind",
    "breadcrumbs",
  ];
  if (fields.some((field) => !sameJson(previous[field], next[field]))) {
    throw new Error("DSM continuation changed its path, temporal cut, or applied selection.");
  }
  const previousEntities = new Set(previous.bands.map((band) => (
    `${band.entity_ref?.entity_kind ?? "none"}:${band.entity_ref?.id ?? "none"}`
  )));
  if (next.bands.some((band) => previousEntities.has(
    `${band.entity_ref?.entity_kind ?? "none"}:${band.entity_ref?.id ?? "none"}`,
  ))) {
    throw new Error("DSM continuation pages overlap.");
  }
}

/** Verify one signed cluster drill advances exactly one disclosed level. */
export function validateLineageDsmDrill(
  parent: LineageDsmResult,
  child: LineageDsmResult,
  drillToken: string,
): void {
  const band = parent.bands.find((candidate) => candidate.drill_token === drillToken);
  if (parent.level_kind !== "clusters" || !band || band.entity_kind !== "cluster" || !band.drill_kind) {
    throw new Error("DSM drill token does not belong to the visible cluster matrix.");
  }
  if (child.level !== parent.level + 1 || child.level_kind !== band.drill_kind) {
    throw new Error("DSM drill did not advance exactly one server-declared hierarchy level.");
  }
  const expectedBreadcrumbs = [
    ...parent.breadcrumbs,
    { level: parent.level, band_id: band.id, label: compactLineageDsmBreadcrumbLabel(band.label) },
  ];
  if (!sameJson(child.breadcrumbs, expectedBreadcrumbs)) {
    throw new Error("DSM drill breadcrumbs do not extend the visible path.");
  }
  const fields: Array<keyof LineageDsmResult> = [
    "contract_version",
    "resource_limits_version",
    "project_id",
    "matrix_kind",
    "aggregation_boundary",
    "applied_selection",
    "effective_as_of",
    "recorded_as_of",
    "snapshot_token",
  ];
  if (fields.some((field) => !sameJson(parent[field], child[field]))) {
    throw new Error("DSM drill changed its temporal cut or applied selection.");
  }
}

function exactCutMatches(request: LineageDsmRequest, result: LineageDsmResult): boolean {
  return typeof request.as_of === "string"
    && request.recorded_as_of !== undefined
    && request.snapshot_token !== undefined
    && compareInstantsAtMicrosecondPrecision(request.as_of, result.effective_as_of) === 0
    && request.recorded_as_of === result.recorded_as_of
    && request.snapshot_token === result.snapshot_token;
}

export function useLineageDsm(descriptor: LineageDsmDescriptor, enabled: boolean): LineageDsmState {
  const continuationByCursorRef = useRef(new Map<string, LineageDsmResult>());
  const parentByDrillTokenRef = useRef(new Map<string, LineageDsmResult>());
  const requestResult = useMemo(() => {
    try {
      return { request: buildLineageDsmRequest(descriptor), error: null };
    } catch (error) {
      return { request: null, error: error instanceof Error ? error : new Error(String(error)) };
    }
  }, [
    descriptor.asOf,
    descriptor.cursor,
    descriptor.drillToken,
    descriptor.filters,
    descriptor.matrixKind,
    descriptor.projectId,
    descriptor.recordedAsOf,
    descriptor.searchQuery,
    descriptor.selectedId,
    descriptor.snapshotToken,
  ]);
  const request = requestResult.request;
  const query = useQuery({
    queryKey: ["lineage-dsm", request],
    queryFn: async ({ signal }) => {
      if (!request) throw new Error("DSM request is invalid.");
      const envelope = await fetchLineageDsm(request, { signal });
      const drillToken = request.drill_token;
      if (drillToken && !request.cursor) {
        const parent = parentByDrillTokenRef.current.get(drillToken);
        if (parent) validateLineageDsmDrill(parent, envelope.data, drillToken);
      }
      if (drillToken && request.cursor) {
        const previous = continuationByCursorRef.current.get(`${drillToken}\u0000${request.cursor}`);
        if (previous) validateLineageDsmContinuation(previous, envelope.data, request.cursor);
      }
      if (drillToken && envelope.data.next_cursor) {
        const key = `${drillToken}\u0000${envelope.data.next_cursor}`;
        continuationByCursorRef.current.delete(key);
        continuationByCursorRef.current.set(key, envelope.data);
        while (continuationByCursorRef.current.size > 32) {
          const oldest = continuationByCursorRef.current.keys().next().value as string | undefined;
          if (oldest === undefined) break;
          continuationByCursorRef.current.delete(oldest);
        }
      }
      for (const band of envelope.data.bands) {
        if (band.entity_kind !== "cluster" || !band.drill_token) continue;
        parentByDrillTokenRef.current.delete(band.drill_token);
        parentByDrillTokenRef.current.set(band.drill_token, envelope.data);
      }
      while (parentByDrillTokenRef.current.size > 256) {
        const oldest = parentByDrillTokenRef.current.keys().next().value as string | undefined;
        if (oldest === undefined) break;
        parentByDrillTokenRef.current.delete(oldest);
      }
      return envelope;
    },
    enabled: enabled && request !== null && requestResult.error === null,
    staleTime: 0,
    retry: false,
  });
  const rawResult = query.data?.data ?? null;
  const issuedCut = rawResult ? {
    effectiveAsOf: rawResult.effective_as_of,
    recordedAsOf: rawResult.recorded_as_of,
    snapshotToken: rawResult.snapshot_token,
  } : null;
  const isExactSettled = Boolean(
    enabled
    && request
    && rawResult
    && exactCutMatches(request, rawResult)
    && !query.isPending
    && !query.isFetching
    && query.error === null,
  );
  return {
    // The unbound server-now response only issues the trio. It never paints,
    // and a request/path transition cannot retain the previous matrix.
    result: isExactSettled ? rawResult : null,
    issuedCut,
    isPending: enabled && requestResult.error === null && query.isPending,
    isFetching: enabled && requestResult.error === null && query.isFetching,
    isExactSettled,
    error: requestResult.error ?? (query.error instanceof Error ? query.error : query.error ? new Error(String(query.error)) : null),
    refresh: query.refetch,
  };
}
